import * as XLSX from "xlsx"
import type {
  CoPurchasePair,
  FocusProductSummary,
  BundleAnalysis,
  CrossSellViewMode,
} from "./types"

interface ExportData {
  mode: CrossSellViewMode
  pairs: CoPurchasePair[]
  focus: FocusProductSummary | null
  bundle: BundleAnalysis | null
  slotLabels?: (string | null)[]
}

/**
 * Export cross-sell analysis data to a multi-sheet Excel file.
 * Exports only the currently active view's data.
 */
export function exportCrossSellToExcel(
  data: ExportData,
  periodLabel: string,
): void {
  const workbook = XLSX.utils.book_new()

  switch (data.mode) {
    case "pairs":
      appendTopPairsSheet(workbook, data.pairs)
      break
    case "focus":
      if (data.focus) {
        appendFocusSummarySheet(workbook, data.focus)
        appendCompanionsSheet(workbook, data.focus)
      }
      break
    case "bundle":
      if (data.bundle) {
        appendBundleSummarySheet(workbook, data.bundle, data.slotLabels)
        appendBundleGroupsSheet(workbook, data.bundle, data.slotLabels)
      }
      break
    case "compare":
      // Compare Bundles export not yet implemented (Phase 4b MVP). The
      // toolbar disables Export for this mode via hasData=false, so this
      // branch is a defense-in-depth no-op.
      return
  }

  const filename = `cross-sell-${data.mode}-${periodLabel}.xlsx`
  XLSX.writeFile(workbook, filename)
}

// ---------------------------------------------------------------------------
// Top Pairs
// ---------------------------------------------------------------------------

function appendTopPairsSheet(wb: XLSX.WorkBook, pairs: CoPurchasePair[]): void {
  const rows = pairs.map((p, i) => ({
    "#": i + 1,
    "Product A": p.product_a_title,
    "Product A SKU": p.product_a_sku ?? "",
    "Product A Orders": p.product_a_total_orders,
    "Product B": p.product_b_title,
    "Product B SKU": p.product_b_sku ?? "",
    "Product B Orders": p.product_b_total_orders,
    "Co-purchases": p.co_purchase_count,
    "Combined Revenue": p.combined_revenue,
    "A → B rate": `${p.pct_of_product_a_orders.toFixed(1)}%`,
    "B → A rate": `${p.pct_of_product_b_orders.toFixed(1)}%`,
  }))

  const sheet = XLSX.utils.json_to_sheet(rows)
  XLSX.utils.book_append_sheet(wb, sheet, "Top Pairs")
}

// ---------------------------------------------------------------------------
// Focus Product
// ---------------------------------------------------------------------------

function appendFocusSummarySheet(
  wb: XLSX.WorkBook,
  focus: FocusProductSummary,
): void {
  const soloOrdersPct =
    focus.total_orders > 0
      ? ((focus.solo_orders / focus.total_orders) * 100).toFixed(1)
      : "0.0"
  const bundleOrdersPct =
    focus.total_orders > 0
      ? ((focus.bundle_orders / focus.total_orders) * 100).toFixed(1)
      : "0.0"
  const uplift =
    focus.solo_aov > 0
      ? (((focus.bundle_aov - focus.solo_aov) / focus.solo_aov) * 100).toFixed(1)
      : "0.0"

  const rows = [
    { Metric: "Focus Product", Value: focus.title },
    { Metric: "Total Orders", Value: focus.total_orders },
    { Metric: "Solo Orders", Value: `${focus.solo_orders} (${soloOrdersPct}%)` },
    { Metric: "Multi-item Orders", Value: `${focus.bundle_orders} (${bundleOrdersPct}%)` },
    { Metric: "Solo Avg Order Value", Value: focus.solo_aov.toFixed(2) },
    { Metric: "Multi-item Avg Order Value", Value: focus.bundle_aov.toFixed(2) },
    { Metric: "Multi-item Uplift", Value: `${uplift}%` },
    { Metric: "Solo Revenue", Value: focus.solo_revenue.toFixed(2) },
    { Metric: "Multi-item Revenue", Value: focus.bundle_revenue.toFixed(2) },
  ]

  const sheet = XLSX.utils.json_to_sheet(rows)
  XLSX.utils.book_append_sheet(wb, sheet, "Focus Summary")
}

function appendCompanionsSheet(
  wb: XLSX.WorkBook,
  focus: FocusProductSummary,
): void {
  const rows = focus.companions.map((c, i) => ({
    "#": i + 1,
    "Companion Product": c.title,
    SKU: c.sku ?? "",
    "Times Together": c.times_bought_together,
    "% of Orders": `${c.pct_of_focus_orders.toFixed(1)}%`,
    "Combined Revenue": c.revenue_together.toFixed(2),
    "Companion Revenue": c.companion_revenue.toFixed(2),
  }))

  const sheet = XLSX.utils.json_to_sheet(rows)
  XLSX.utils.book_append_sheet(wb, sheet, "Companion Products")
}

// ---------------------------------------------------------------------------
// Bundle Builder
// ---------------------------------------------------------------------------

function appendBundleSummarySheet(
  wb: XLSX.WorkBook,
  bundle: BundleAnalysis,
  slotLabels?: (string | null)[],
): void {
  const groupNames = bundle.bundle_group_titles.map(
    (title, i) => slotLabels?.[i] || title,
  )

  const rows = [
    { Metric: "Bundle Groups", Value: groupNames.join(" + ") },
    { Metric: "Full Bundle Orders", Value: bundle.full_bundle_orders },
    { Metric: "Full Bundle Avg Order Value", Value: bundle.full_bundle_aov.toFixed(2) },
    { Metric: "Full Bundle Revenue", Value: bundle.full_bundle_revenue.toFixed(2) },
    ...(bundle.bundle_groups.length > 2
      ? [{ Metric: "Partial Overlap Orders", Value: bundle.partial_overlap_orders }]
      : []),
    { Metric: "Any Group Orders", Value: bundle.total_orders_with_any },
  ]

  const sheet = XLSX.utils.json_to_sheet(rows)
  XLSX.utils.book_append_sheet(wb, sheet, "Bundle Summary")
}

function appendBundleGroupsSheet(
  wb: XLSX.WorkBook,
  bundle: BundleAnalysis,
  slotLabels?: (string | null)[],
): void {
  const rows = bundle.group_stats.map((g, i) => {
    const label = slotLabels?.[i] || g.title
    const inBundle = g.total_orders - g.solo_orders

    return {
      Slot: `Slot ${i + 1}`,
      Label: label,
      SKU: g.sku ?? "",
      "Total Orders": g.total_orders,
      "In Bundle": inBundle,
      "Without Bundle": g.solo_orders,
      "Without Bundle Revenue": g.solo_revenue.toFixed(2),
    }
  })

  const sheet = XLSX.utils.json_to_sheet(rows)
  XLSX.utils.book_append_sheet(wb, sheet, "Group Stats")
}
